import React, { useState, useEffect } from 'react';
import { PlusIcon, Cog6ToothIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { DatabaseConnectionModal } from '../components/DatabaseConnectionModal';
import { DatabasesList } from '../components/DatabasesList';
import { CompaniesGrid } from '../components/CompaniesGrid';
import { CompanyDetailsModal } from '../components/CompanyDetailsModal';
import { CompanyCreateModal } from '../components/CompanyCreateModal';
import { AsaasChargesPanel } from '../components/AsaasChargesPanel';
import { InformativoTab } from '../components/InformativoTab';
import { AnnouncementListTab } from '../components/AnnouncementListTab';
import { AnnouncementsPanel } from '../components/AnnouncementsPanel';
import { MonitorPanel } from '../components/MonitorPanel';
import { PM2Monitor } from '../components/PM2Monitor';
import { useDatabases } from '../hooks/useDatabases';
import { useCompanies } from '../hooks/useCompanies';

/**
 * Main Dashboard Page
 */
export default function Dashboard({ onLogout }) {
  const { databases, list: listDatabases } = useDatabases();
  const { update: updateCompany, remove: deleteCompany } = useCompanies();
  
  const [activeTab, setActiveTab] = useState('companies'); // 'companies', 'databases', 'monitor', or 'pm2-monitor'
  const [selectedDatabase, setSelectedDatabase] = useState('consolidated');
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAsaasPanel, setShowAsaasPanel] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    listDatabases();
  }, [listDatabases]);

  const handleAddDatabase = () => {
    setShowConnectionModal(true);
  };

  const handleDatabasesRefresh = () => {
    listDatabases();
  };

  const handleCreateCompany = () => {
    if (selectedDatabase === 'consolidated') {
      toast.error('Por favor, selecione um banco de dados específico para criar empresa');
      return;
    }
    setShowCreateModal(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  const handleDeleteCompany = (company) => {
    setCompanyToDelete(company);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!companyToDelete || !companyToDelete._database?.id) {
      toast.error('Unable to delete: Database information missing');
      return;
    }

    setIsLoading(true);
    try {
      await deleteCompany(
        companyToDelete._database.id,
        companyToDelete.id
      );
      setShowDeleteConfirm(false);
      setCompanyToDelete(null);
      toast.success('Company deleted successfully');
      // Refresh the list
      handleDatabasesRefresh();
    } catch (error) {
      toast.error(`Failed to delete company: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAsaasPanel = (company) => {
    setSelectedCompany(company);
    setShowAsaasPanel(true);
  };

  const handleUpdateCompany = async (updatedData) => {
    if (!selectedCompany?._database?.id) {
      toast.error('Database ID not found');
      return;
    }

    setIsLoading(true);
    try {
      await updateCompany(
        selectedCompany._database.id,
        selectedCompany.id,
        updatedData
      );
      setShowCompanyModal(false);
      setSelectedCompany(null);
      toast.success('Company updated successfully');
      // Optionally refresh the list
      handleDatabasesRefresh();
    } catch (error) {
      toast.error(`Failed to update company: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Gerenciador de Empresas
              </h1>
              <p className="mt-2 text-gray-600">
                Gerencie empresas em múltiplos bancos de dados de forma centralizada
              </p>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md transition"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('companies')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'companies'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Empresas
            </button>
            <button
              onClick={() => setActiveTab('databases')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'databases'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Conexões de Banco
            </button>
            <button
              onClick={() => setActiveTab('monitor')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'monitor'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Monitor
            </button>
            <button
              onClick={() => setActiveTab('informativo')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'informativo'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Informativo
            </button>
            <button
              onClick={() => setActiveTab('pm2-monitor')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'pm2-monitor'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Monitor Node
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'companies' && (
          /* Companies Tab */
          <div className="space-y-6">
            {/* Database Selector and Create Button */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col md:flex-row md:items-end md:gap-4 gap-4">
                <div className="flex-1">
                  <label className="block text-base font-semibold text-gray-900 mb-3">
                    Selecione um Banco de Dados
                  </label>
                  <select
                    value={selectedDatabase}
                    onChange={(e) => setSelectedDatabase(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                  >
                    <option value="consolidated">
                      Todos os Bancos (Consolidado)
                    </option>
                    {databases && databases.length > 0 ? (
                      databases.filter(db => db.isActive !== false).map((db) => (
                        <option key={db.id} value={db.id}>
                          {db.displayName || db.dbName}
                        </option>
                      ))
                    ) : (
                      <option disabled>Nenhum banco ativo encontrado</option>
                    )}
                  </select>
                  {databases && databases.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600">
                      ℹ️ Nenhum banco de dados configurado. Acesse "Conexões de Banco" para adicionar.
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCreateCompany}
                  disabled={selectedDatabase === 'consolidated' || !databases || databases.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition text-base font-medium"
                >
                  <PlusIcon className="w-5 h-5" />
                  Nova Empresa
                </button>
              </div>
            </div>

            {/* Companies Grid */}
            <div className="bg-white rounded-lg shadow">
              <CompaniesGrid
                databaseId={selectedDatabase}
                onEditCompany={handleEditCompany}
                onDeleteCompany={handleDeleteCompany}
                onAsaasPanel={handleAsaasPanel}
              />
            </div>
          </div>
        )}

        {activeTab === 'databases' && (
          /* Databases Tab */
          <div className="space-y-6">
            {/* Add Database Button */}
            <div className="flex justify-end mb-6">
              <button
                onClick={handleAddDatabase}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              >
                <PlusIcon className="w-5 h-5" />
                Adicionar Nova Conexão
              </button>
            </div>

            {/* Databases List */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
                Configurações de Banco de Dados
              </h2>
              <DatabasesList onEdit={() => {}} />
            </div>
          </div>
        )}

        {activeTab === 'monitor' && (
          /* Monitor Tab */
          <MonitorPanel />
        )}

        {activeTab === 'informativo' && (
          <AnnouncementsPanel />
        )}

        {activeTab === 'pm2-monitor' && (
          /* PM2 Monitor Tab */
          <PM2Monitor />
        )}
      </main>

      {/* Modals */}
      <DatabaseConnectionModal
        isOpen={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
        onSuccess={handleDatabasesRefresh}
      />

      <CompanyDetailsModal
        company={selectedCompany}
        isOpen={showCompanyModal}
        onClose={() => {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }}
        onUpdate={handleUpdateCompany}
      />

      <CompanyCreateModal
        databaseId={selectedDatabase}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          toast.success('Empresa criada com sucesso');
          handleDatabasesRefresh();
        }}
      />

      <AsaasChargesPanel
        company={selectedCompany}
        isOpen={showAsaasPanel}
        onClose={() => {
          setShowAsaasPanel(false);
          setSelectedCompany(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && companyToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Deletar Empresa
            </h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja deletar <strong>{companyToDelete.name}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isLoading}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition disabled:opacity-50"
              >
                {isLoading ? 'Deletando...' : 'Deletar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
